import { DownOutlined, EyeOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, Input, Row, Select, Space, Table, Tag } from 'antd';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { ClaimStatus } from './enums/claim-status.enum';
import { Dayjs } from 'dayjs';
import { DateHelper } from 'helper/date.helper';
import { EnumHelper } from 'helper/enum.helper';
import { DisputeReason } from './enums/dispute-reason.enum';
import useGetClaims from './hooks/get-claims.hook';
import { IClaimItem } from './interfaces/IClaimItem';
import PageWithFilters from 'components/page/page-with-filters';
import { PageWithFiltersContentContainer } from 'components/page/page.styles';
import withAuthorization from 'components/hocs/withAuthorization';
import { RealmRoles } from '../../constants';
import { getDateFormatted } from 'utils/date';
import ClaimStatusTag from './components/claim-status-tag';
import styled from 'styled-components';



export const ActionsContainer = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const { RangePicker } = DatePicker;

export type SearchClaimsFilters = {
  dateRanges: [Dayjs, Dayjs];
  lastFour?: string;
  cuil?: string;
  status?: ClaimStatus;
  disputeReason?: string;
};

const emptyFilterValues = {
    dateRanges: DateHelper.getDayJsLastMonthRange(),
    lastFour: '',
    cuil: '',
    status: null,
    disputeReason: ''
}


const ClaimsFilters = ({ onSubmit }: { onSubmit: (data: SearchClaimsFilters) => void }) => {
  const [form] = Form.useForm<SearchClaimsFilters>();
  const [expand, setExpand] = useState(false);

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
  };
 
  const onFinish = (values: SearchClaimsFilters) => {
    onSubmit(values);
  };

  const onReset = () => {
    onSubmit(emptyFilterValues);
  };

  return (
    <Form
      form={form}
      name="advanced_search"
      initialValues={{ dateRanges: DateHelper.getDayJsLastMonthRange()}}
      style={formStyle}
      onFinish={onFinish}
      onReset={onReset}
    >
      <Row gutter={24}>
        <Col span={8}>
            <Form.Item<SearchClaimsFilters> name="dateRanges" label="Fechas">
               <RangePicker style={{ width: '100%' }} />
            </Form.Item>
        </Col>
        <Col span={8}>
              <Form.Item<SearchClaimsFilters> name="status" label="Estado">
                <Select
                  placeholder="Seleccionar"
                  options={EnumHelper.toSelectOptions(ClaimStatus)}
                />
              </Form.Item>
        </Col>
        <Col span={8}>
              <Form.Item<SearchClaimsFilters> name="disputeReason" label="Dispute Reason">
                <Select
                  placeholder="Seleccionar"
                  options={EnumHelper.toSelectOptions(DisputeReason)}
                />
              </Form.Item>
        </Col>

        {!expand && ( <>
          <Col span={24}>
            <div style={{ textAlign: 'right' }}>
              <Space size="small">
                <Button type="primary" htmlType="submit">
                  Buscar
                </Button>
                <Button htmlType="reset">Limpiar</Button>
                <a
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    setExpand(!expand);
                  }}
                >
                  <DownOutlined rotate={expand ? 180 : 0} /> Más
                </a>
              </Space>
            </div>
          </Col>
        </>
        )}
                {expand && (
          <>
          <Col span={8}>
            <Form.Item<SearchClaimsFilters>
              name="cuil"
              label="CUIL/CUIT"
            >
              <Input placeholder="Identificación del tarjeta habiente" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<SearchClaimsFilters>
              name="lastFour"
              label="Tarjeta"
            >
              <Input maxLength={4} placeholder="Ultimos 4 números" />
            </Form.Item>
          </Col>
            <Col span={24}>
              <div style={{ textAlign: 'right' }}>
                <Space size="small">
                  <Button type="primary" htmlType="submit">
                    Buscar
                  </Button>
                  <Button htmlType="reset">Limpiar</Button>
                  <a
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      setExpand(!expand);
                    }}
                  >
                    <DownOutlined rotate={expand ? 180 : 0} /> Menos
                  </a>
                </Space>
              </div>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
};

const Claims = () => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();

  const [claimsFilter, setClaimsFilter] = useState(emptyFilterValues);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const { claims, pageInfo, isLoading } = useGetClaims(
    paginationModel.page,
    paginationModel.pageSize,
    claimsFilter
  );

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  const handleViewClaimDetailClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, claimId: string) => {
    e.preventDefault();
    navigate(`/${issuer}/claims/${claimId}`);
  };

  const handleSearchClick = (data: SearchClaimsFilters) => {
    setClaimsFilter({
      dateRanges: data.dateRanges,
      disputeReason: data.disputeReason,
      status: data.status,
      lastFour: data.lastFour,
      cuil: data.cuil
    });
  };


  const columns: ColumnsType<IClaimItem> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'ID',
    },
    {
      key: 'created_date',
      dataIndex: 'created_date',
      title: 'Fecha',
      render: (_, record) => {
        return getDateFormatted(record.created_date);
      },
    },
    {
      key: 'dispute_reason',
      dataIndex: 'dispute_reason',
      title: 'Dispute Reason',
      render: (_, record) => {
        return record?.dispute_reason;
      },
    },
    {
      key: 'status',
      dataIndex: 'status',
      title: 'Estado',
      render: (_, record) => {
        return <ClaimStatusTag status={record.status} />;
      },
    },
    {
      key: 'cardholder',
      dataIndex: 'cardholder',
      title: 'Tarjetahabiente',
      render: (_, record) => {
        return record?.cardholder?.last_name + " " +  record?.cardholder?.first_name
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            onClick={(e) => handleViewClaimDetailClick(e, record.id)}
            size="small"
            type="link"
            icon={<EyeOutlined />}
          >
            Detalles
          </Button>
        </Space>
      ),
    },
  ];

  const NewClaimBtn = withAuthorization([RealmRoles.TransactionClaimsWrite], () => (
    <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate(`/${issuer}/claims/new`)} >
      <Space>
        Nuevo
      </Space>
    </Button>
  ));

  return (
    <PageWithFilters title="Reclamos">
      <PageWithFiltersContentContainer>
        <ClaimsFilters onSubmit={handleSearchClick} />
      </PageWithFiltersContentContainer>
      <PageWithFiltersContentContainer>
        <ActionsContainer>
          <NewClaimBtn />
        </ActionsContainer>
        <Table
          dataSource={claims ?? []}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: pageInfo?.total_rows,
            showTotal: (total) => pageInfo?.total !== undefined ? `Total: ${total}` : ``,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
    </PageWithFilters>
  );
};

export default withAuthorization([RealmRoles.TransactionClaimsRead, RealmRoles.TransactionClaimsWrite,  RealmRoles.TransactionClaimsManagement ], Claims);
